// src/components/CierreMes.jsx
import { useMemo, useState, useEffect } from "react";
import { descargarFacturasZip } from "../api";

export default function CierreMes({
  mes,
  setMes,
  pagos,
  resumen, // compatibilidad
  filtroEspecialidades,
  setFiltroEspecialidades,
  specialtiesOptions,
  loading,
  error,
}) {
  // 1) Filtro por especialidad (tabla/resumen)
  const filtrarPorEspecialidad = (items) =>
    items.filter((p) => {
      const code = (p.specialty_code || "").toLowerCase();
      if (!code) return true;
      if (filtroEspecialidades[code] === undefined) return true;
      return filtroEspecialidades[code];
    });

  const pagosFiltrados = filtrarPorEspecialidad(pagos);

  // =========================================================
  // ✅ CUADRO PARA DESCARGA (independiente del filtro del mes)
  // =========================================================

  const [dlTarjeta, setDlTarjeta] = useState(true);
  const [dlEfectivo, setDlEfectivo] = useState(true);

  const [dlEspecialidades, setDlEspecialidades] = useState({});

  useEffect(() => {
    setDlEspecialidades((prev) => {
      const next = { ...prev };
      (specialtiesOptions || []).forEach((s) => {
        const k = String(s.code).toLowerCase();
        if (next[k] === undefined) next[k] = true;
      });
      return next;
    });
  }, [specialtiesOptions]);

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [downloadInfo, setDownloadInfo] = useState("");

  // Pagos seleccionados SOLO para descarga (no afecta tabla)
  const pagosParaDescarga = useMemo(() => {
    return (pagos || []).filter((p) => {
      const code = String(p.specialty_code || "").toLowerCase();
      const method = String(p.payment_method || "").toLowerCase();

      if (code && dlEspecialidades[code] === false) return false;

      if (method === "tarjeta" && !dlTarjeta) return false;
      if (method === "efectivo" && !dlEfectivo) return false;

      return true;
    });
  }, [pagos, dlEspecialidades, dlTarjeta, dlEfectivo]);

  // =========================================================
  // ✅ Descargar ZIP (BACKEND: dompdf + plantilla + IVA yoga)
  // =========================================================
  const onDescargarZip = async () => {
    try {
      setDownloading(true);
      setDownloadError("");
      setDownloadInfo("");

      if (!mes) throw new Error("No hay mes seleccionado");

      const ids = (pagosParaDescarga || [])
        .map((p) => Number(p.id))
        .filter((x) => Number.isFinite(x) && x > 0);

      if (!ids.length) throw new Error("No hay pagos para descargar");

      // Una sola request al backend (invoices.zip.php)
      await descargarFacturasZip({ month: mes, paymentIds: ids });

      setDownloadInfo(`ZIP generado con ${ids.length} pagos.`);
    } catch (e) {
      setDownloadError(e?.message || "Error generando ZIP");
    } finally {
      setDownloading(false);
    }
  };

  // 2) Resumen dinámico según filtros (total / efectivo / tarjeta / nº pagos)
  const calcularResumen = (items) => {
    let total = 0;
    let efectivo = 0;
    let tarjeta = 0;

    items.forEach((p) => {
      const amt = Number(p.amount) || 0;
      total += amt;
      if (p.payment_method === "efectivo") efectivo += amt;
      else if (p.payment_method === "tarjeta") tarjeta += amt;
    });

    return { total, efectivo, tarjeta, count: items.length };
  };

  const resumenFiltrado = calcularResumen(pagosFiltrados);

  // 3) Comisiones por especialidad para el centro
  const calcularComisionCentro = (items, code, porcentajeCentro) => {
    let suma = 0;
    items.forEach((p) => {
      const c = (p.specialty_code || "").toLowerCase();
      if (c === code) suma += Number(p.amount) || 0;
    });
    return suma * porcentajeCentro;
  };

  // Logopedia / nutrición → 30% centro
  const comisionLogopedia = calcularComisionCentro(pagosFiltrados, "logopeda", 0.3);
  const comisionNutricion = calcularComisionCentro(pagosFiltrados, "nutricion", 0.3);

  // Psicología adultos → 40% centro
  const comisionPsicoAdultos = calcularComisionCentro(
    pagosFiltrados,
    "psicologia-adultos",
    0.4
  );

  // 4) Total REAL del centro:
  const calcularTotalRealCentro = (items) => {
    let total = 0;
    let efectivo = 0;
    let tarjeta = 0;

    items.forEach((p) => {
      const amt = Number(p.amount) || 0;
      const code = (p.specialty_code || "").toLowerCase();

      let factor = 1; // por defecto 100% centro
      if (code === "logopeda" || code === "nutricion") factor = 0.3;
      else if (code === "psicologia-adultos") factor = 0.4;

      const real = amt * factor;
      total += real;

      if (p.payment_method === "efectivo") efectivo += real;
      else if (p.payment_method === "tarjeta") tarjeta += real;
    });

    return { total, efectivo, tarjeta };
  };

  const totalRealCentro = calcularTotalRealCentro(pagosFiltrados);

  return (
    <div className="cierre-panel">
      <div className="pagos-actions">
        <div className="month-filter">
          <label>
            Mes:
            <input type="month" value={mes} onChange={(e) => setMes(e.target.value)} />
          </label>
        </div>

        {/* ✅ FILTRO ORIGINAL (TABLA/RESUMEN) */}
        <div className="especialidad-filter">
          {specialtiesOptions.map((s) => (
            <label key={s.code} className="chk-especialidad">
              <input
                type="checkbox"
                checked={!!filtroEspecialidades[s.code]}
                onChange={(e) =>
                  setFiltroEspecialidades((prev) => ({
                    ...prev,
                    [s.code]: e.target.checked,
                  }))
                }
              />
              {s.label}
            </label>
          ))}
        </div>
      </div>

      {/* ✅ CUADRO DESCARGA ZIP (NO afecta a tabla) */}
      <div
        className="cierre-panel"
        style={{ marginTop: 12, padding: 12, border: "1px solid #ddd", borderRadius: 10 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong>Descargar facturas del mes ({mes})</strong>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Estos filtros solo afectan al ZIP, no a la tabla. (IVA yoga y exención se calculan en backend)
            </div>
          </div>

          <button
            type="button"
            className="btn"
            onClick={onDescargarZip}
            disabled={downloading || loading || pagosParaDescarga.length === 0}
          >
            {downloading ? "Generando ZIP..." : "Descargar ZIP"}
          </button>
        </div>

        {/* Métodos de pago (solo descarga) */}
        <div style={{ marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap" }}>
          <label className="chk-especialidad">
            <input
              type="checkbox"
              checked={dlTarjeta}
              onChange={(e) => setDlTarjeta(e.target.checked)}
            />
            💳 Tarjeta
          </label>
          <label className="chk-especialidad">
            <input
              type="checkbox"
              checked={dlEfectivo}
              onChange={(e) => setDlEfectivo(e.target.checked)}
            />
            💵 Efectivo
          </label>
        </div>

        {/* Especialidades (solo descarga) */}
        <div className="especialidad-filter" style={{ marginTop: 10 }}>
          {specialtiesOptions.map((s) => {
            const code = String(s.code).toLowerCase();
            return (
              <label key={`dl-${s.code}`} className="chk-especialidad">
                <input
                  type="checkbox"
                  checked={dlEspecialidades[code] !== false}
                  onChange={(e) =>
                    setDlEspecialidades((prev) => ({
                      ...prev,
                      [code]: e.target.checked,
                    }))
                  }
                />
                {s.label}
              </label>
            );
          })}
        </div>

        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
          Facturas incluidas: <strong>{pagosParaDescarga.length}</strong>
        </div>

        {downloadInfo ? <div style={{ marginTop: 8, fontSize: 12 }}>{downloadInfo}</div> : null}
        {downloadError ? <div style={{ marginTop: 8, color: "red", whiteSpace: "pre-wrap" }}>{downloadError}</div> : null}
      </div>

      {loading ? (
        <p>Cargando cierre de mes...</p>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : (
        <>
          <div className="cierre-resumen">
            <div className="cierre-card">
              <strong>Total mes:</strong> {resumenFiltrado.total.toFixed(2)} €
            </div>
            <div className="cierre-card">
              <strong>Efectivo:</strong> {resumenFiltrado.efectivo.toFixed(2)} €
            </div>
            <div className="cierre-card">
              <strong>Tarjeta:</strong> {resumenFiltrado.tarjeta.toFixed(2)} €
            </div>
            <div className="cierre-card">
              <strong>Nº pagos:</strong> {resumenFiltrado.count}
            </div>

            <div className="cierre-card">
              <strong>Logopedia 30% (centro):</strong> {comisionLogopedia.toFixed(2)} €
            </div>
            <div className="cierre-card">
              <strong>Nutrición 30% (centro):</strong> {comisionNutricion.toFixed(2)} €
            </div>
            <div className="cierre-card">
              <strong>Psicología Adultos 40% (centro):</strong> {comisionPsicoAdultos.toFixed(2)} €
            </div>

            <div className="cierre-card cierre-card-destacado">
              <strong>Total real centro:</strong> {totalRealCentro.total.toFixed(2)} €
            </div>
          </div>

          {pagosFiltrados.length === 0 ? (
            <p>No hay pagos en este mes.</p>
          ) : (
            <table className="pv-table pagos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Paciente</th>
                  <th>Especialidad</th>
                  <th>Servicio</th>
                  <th>Importe (€)</th>
                  <th>Método</th>
                </tr>
              </thead>
              <tbody>
                {pagosFiltrados.map((p) => (
                  <tr key={p.id}>
                    <td>{new Date(p.date).toLocaleDateString("es-ES")}</td>
                    <td className="paciente-cell" title={`${p.paciente} ${p.apellidos || ""}`}>
                      <strong>{p.paciente}</strong> {p.apellidos}
                    </td>
                    <td>{p.specialty_name || p.specialty_code || "—"}</td>
                    <td>{p.service_name || "-"}</td>
                    <td style={{ textAlign: "center" }}>{p.amount} €</td>
                    <td>{p.payment_method === "efectivo" ? "💵 Efectivo" : "💳 Tarjeta"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
